<template>
  <div class="runFooter">
    <!-- Action-run status counts (action nodes only) -->
    <div v-if="actionRunCountEntries.length > 0" class="actionRunCountsRow" @click.stop>
      <button
        v-for="entry in actionRunCountEntries"
        :key="entry.status"
        type="button"
        class="actionRunCountPill"
        :class="{ 'actionRunCountPill--clickable': entry.status === 'needs_attention' }"
        :disabled="entry.status !== 'needs_attention'"
        @click="entry.status === 'needs_attention' && emit('openNeedsAttention')"
      >
        <span class="actionRunCountDot" :class="`actionRunCount--${entry.status}`" />
        <span>{{ entry.label }} {{ entry.count }}</span>
      </button>
    </div>

    <!-- Last run summary + status indicator -->
    <div v-if="lastRunSummary || latestRun || hasActiveRuns" class="lastRunRow">
      <span v-if="lastRunSummary" class="lastRun">{{ lastRunSummary }}</span>
      <!-- Running/queued dot counters (auto-right-aligns) -->
      <NavBadge
        v-if="hasActiveRuns"
        variant="activity"
        :running="runCounts!.running"
        :queued="runCounts!.queued"
        :divider="false"
        style="margin-left: auto"
      />
      <!-- Single status pill (for source + filter cards) -->
      <router-link
        v-else-if="latestRun"
        :to="`/agent-runs/${latestRun.id}`"
        class="lastRunPillLink"
        @click.stop
      >
        <AgentRunStatusPill :status="latestRun.status" />
      </router-link>
    </div>

    <div class="runFooterControls" @click.stop>
      <!-- Run Now split button -->
      <div class="splitBtn" v-if="showRunNow">
        <button
          class="splitBtnMain"
          type="button"
          :disabled="running"
          @click="$emit('run')"
        >
          <template v-if="running">
            <AppIcon name="stop" :size="14" />
            <span>Stop</span>
          </template>
          <template v-else>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M2.5 1.5 L10 6 L2.5 10.5Z" fill="currentColor" />
            </svg>
            <span>Run Now {{ recordLimitLabel }}</span>
          </template>
        </button>
        <button
          class="splitBtnCaret"
          type="button"
          :disabled="running"
          @click="recordLimitDropdownOpen = !recordLimitDropdownOpen"
          ref="recordLimitCaretRef"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M2 3.5 L5 6.5 L8 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div v-if="recordLimitDropdownOpen" class="splitDropdown" ref="recordLimitDropdownRef">
          <button
            v-for="opt in recordOptions"
            :key="opt.value"
            class="splitDropdownItem"
            :class="{ 'splitDropdownItem--active': opt.value === recordLimit }"
            type="button"
            @click="selectLimit(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- Auto-run toggle -->
      <div class="autoToggle" v-if="showAutoRun">
        <span class="autoLabel">Auto</span>
        <ToggleSwitch :model-value="autoRun" @update:model-value="$emit('update:autoRun', $event)" />
        <span v-if="cadenceLabel" class="cadenceLabel">{{ cadenceLabel }}</span>
      </div>

      <!-- Auto-run tri-state (prospecting node): off | once | cadence -->
      <div class="autoToggle" v-if="showAutoRunMode">
        <span class="autoLabel">Auto</span>
        <div class="splitBtn">
          <button
            class="splitBtnMain"
            type="button"
            @click="autoRunModeDropdownOpen = !autoRunModeDropdownOpen"
            ref="autoRunModeCaretRef"
          >
            <span>{{ autoRunModeLabel }}</span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M2 3.5 L5 6.5 L8 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>

          <div v-if="autoRunModeDropdownOpen" class="splitDropdown" ref="autoRunModeDropdownRef">
            <button
              v-for="opt in autoRunModeOptions"
              :key="opt.value"
              class="splitDropdownItem"
              :class="{ 'splitDropdownItem--active': opt.value === autoRunMode }"
              type="button"
              @click="selectAutoRunMode(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <span v-if="cadenceLabel && autoRunMode === 'cadence'" class="cadenceLabel">{{ cadenceLabel }}</span>
      </div>

      <!-- Auto-approve reachouts (for the sequence node) — tri-state: none | after_first | all -->
      <div class="autoApprove" v-if="showAutoApprove">
        <span class="autoApproveLabel">Auto-approve reachouts</span>
        <button
          v-if="showApprovalWarning"
          class="approvalWarningIcon"
          type="button"
          title="Workspace email settings may override this"
          @click="approvalWarningOpen = true"
        >
          <AppIcon name="error" :size="16" />
        </button>
        <div class="splitBtn">
          <button
            class="splitBtnMain"
            type="button"
            @click="approvePolicyDropdownOpen = !approvePolicyDropdownOpen"
            ref="approvePolicyCaretRef"
          >
            <span>{{ approvePolicyLabel }}</span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M2 3.5 L5 6.5 L8 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>

          <div v-if="approvePolicyDropdownOpen" class="splitDropdown" ref="approvePolicyDropdownRef">
            <button
              v-for="opt in approvePolicyOptions"
              :key="opt.value"
              class="splitDropdownItem"
              :class="{ 'splitDropdownItem--active': opt.value === autoApprovePolicy }"
              type="button"
              @click="selectApprovePolicy(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-if="approvalWarningOpen"
      title="Workspace setting may override this"
      :max-width="420"
      @close="approvalWarningOpen = false"
    >
      <p class="approvalWarningText">
        Your workspace email settings have "Require approval" enabled, which will
        override this play's auto-approve setting.
        <router-link to="/email-settings" @click="approvalWarningOpen = false">Change it in Email Settings</router-link>
      </p>
    </Modal>

    <!-- Confirm before enabling auto-approve: applies only to future reachouts, existing pending_approval rows are untouched. -->
    <Modal
      v-if="pendingApprovePolicy"
      title="Turn on auto-approve?"
      :max-width="440"
      @close="pendingApprovePolicy = null"
    >
      <p class="approveConfirmText">
        This will automatically send future emails and voicemails to contacts
        without any human review.
      </p>
      <p v-if="pendingReachoutsCount" class="approveConfirmText">
        (Note: this will not affect the {{ pendingReachoutsCount }} existing reachout{{ pendingReachoutsCount === 1 ? '' : 's' }} 
        currently pending approval, only new reachouts.)
      </p>
      <template #footer>
        <button class="btn secondary" type="button" @click="pendingApprovePolicy = null">Cancel</button>
        <button class="btn danger" type="button" @click="confirmApprovePolicy">Turn on auto-approve</button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import NavBadge from '../../components/NavBadge.vue'
import AgentRunStatusPill from '../../../components/AgentRunStatusPill.vue'
import AppIcon from '../../../components/AppIcon.vue'
import ToggleSwitch from '../../../components/ToggleSwitch.vue'
import Modal from '../../components/Modal.vue'

const props = defineProps<{
  showRunNow?: boolean
  showAutoRun?: boolean
  showAutoRunMode?: boolean
  showAutoApprove?: boolean
  showApprovalWarning?: boolean
  running?: boolean
  recordLimit?: number | null
  autoRun?: boolean
  autoRunMode?: string
  autoApprovePolicy?: string
  pendingReachoutsCount?: number | null
  lastRunSummary?: string | null
  latestRun?: { id: string; status: string } | null
  runCounts?: { running: number; queued: number } | null
  limitOptions?: { label: string; value: number }[]
  cadenceLabel?: string | null
  /** Per-status SalesPlayActionRun counts for this action node — 'action'
   *  node cards only. Only non-zero buckets render; clicking one deep-links
   *  to the Candidates page filtered to this action + that status. */
  actionRunCounts?: Record<string, number> | null
  actionId?: string | null
  playId?: string | null
}>()

const emit = defineEmits<{
  run: []
  'update:recordLimit': [value: number | null]
  'update:autoRun': [value: boolean]
  'update:autoRunMode': [value: string]
  'update:autoApprovePolicy': [value: string]
  openNeedsAttention: []
}>()

const recordLimitDropdownOpen = ref(false)
const recordLimitCaretRef = ref<HTMLElement | null>(null)
const recordLimitDropdownRef = ref<HTMLElement | null>(null)

const approvePolicyDropdownOpen = ref(false)
const approvePolicyCaretRef = ref<HTMLElement | null>(null)
const approvePolicyDropdownRef = ref<HTMLElement | null>(null)

const autoRunModeDropdownOpen = ref(false)
const autoRunModeCaretRef = ref<HTMLElement | null>(null)
const autoRunModeDropdownRef = ref<HTMLElement | null>(null)

const approvalWarningOpen = ref(false)
const pendingApprovePolicy = ref<string | null>(null)

const ACTION_RUN_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  dispatched: 'Dispatched',
  in_progress: 'In progress',
  needs_attention: 'Needs attention',
  complete: 'Complete',
  cancelled: 'Cancelled',
}

const ACTION_RUN_STATUS_ORDER = ['pending', 'needs_attention']

const actionRunCountEntries = computed(() => {
  const counts = props.actionRunCounts || {}
  return ACTION_RUN_STATUS_ORDER
    .filter((status) => (counts[status] || 0) > 0)
    .map((status) => ({ status, label: ACTION_RUN_STATUS_LABELS[status], count: counts[status] }))
})

const _defaultOptions = [
  { label: '1', value: 1 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]

const recordOptions = computed(() => props.limitOptions || _defaultOptions)

const approvePolicyOptions = [
  { label: 'None', value: 'none' },
  { label: 'After first', value: 'after_first' },
  { label: 'All', value: 'all' },
]

const approvePolicyLabel = computed(() => {
  const match = approvePolicyOptions.find(o => o.value === props.autoApprovePolicy)
  return match?.label || 'None'
})

const autoRunModeOptions = [
  { label: 'Off', value: 'off' },
  { label: 'Once', value: 'once' },
  { label: 'Cadence', value: 'cadence' },
]

const autoRunModeLabel = computed(() => {
  const match = autoRunModeOptions.find(o => o.value === props.autoRunMode)
  return match?.label || 'Off'
})

const hasActiveRuns = computed(() =>
  (props.runCounts?.running || 0) > 0 || (props.runCounts?.queued || 0) > 0
)

const recordLimitLabel = computed(() => {
  return String(props.recordLimit || 10)
})

function selectLimit(value: number | null) {
  emit('update:recordLimit', value)
  recordLimitDropdownOpen.value = false
}

function selectApprovePolicy(value: string) {
  approvePolicyDropdownOpen.value = false
  if (value === 'after_first' || value === 'all') {
    pendingApprovePolicy.value = value
    return
  }
  emit('update:autoApprovePolicy', value)
}

function confirmApprovePolicy() {
  if (!pendingApprovePolicy.value) return
  emit('update:autoApprovePolicy', pendingApprovePolicy.value)
  pendingApprovePolicy.value = null
}

function selectAutoRunMode(value: string) {
  emit('update:autoRunMode', value)
  autoRunModeDropdownOpen.value = false
}

function onDocumentClick(e: MouseEvent) {
  const target = e.target as Node
  if (recordLimitDropdownOpen.value) {
    const inside = recordLimitCaretRef.value?.contains(target) || recordLimitDropdownRef.value?.contains(target)
    if (!inside) recordLimitDropdownOpen.value = false
  }
  if (approvePolicyDropdownOpen.value) {
    const inside = approvePolicyCaretRef.value?.contains(target) || approvePolicyDropdownRef.value?.contains(target)
    if (!inside) approvePolicyDropdownOpen.value = false
  }
  if (autoRunModeDropdownOpen.value) {
    const inside = autoRunModeCaretRef.value?.contains(target) || autoRunModeDropdownRef.value?.contains(target)
    if (!inside) autoRunModeDropdownOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onDocumentClick, true))
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick, true))
</script>

<style scoped>
.runFooter {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border-subtle);
}

.lastRunRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.lastRunPillLink {
  text-decoration: none;
  flex-shrink: 0;
}

.lastRun {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  line-height: var(--lh-normal);
}

.actionRunCountsRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}
.actionRunCountPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 var(--space-2);
  border: none;
  border-radius: var(--radius-full);
  background: var(--color-bg-muted, rgba(0, 0, 0, 0.05));
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: default;
  transition: background var(--duration-fast) var(--ease-out);
}
.actionRunCountPill--clickable {
  cursor: pointer;
}
.actionRunCountPill--clickable:hover {
  background: var(--color-bg-subtle);
}
.actionRunCountDot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.actionRunCount--pending { background: var(--color-text-tertiary); }
.actionRunCount--dispatched { background: var(--color-info, #1d4ed8); }
.actionRunCount--in_progress { background: var(--color-info, #1d4ed8); }
.actionRunCount--needs_attention { background: var(--color-danger, #b91c1c); }
.actionRunCount--complete { background: var(--color-success, #047857); }
.actionRunCount--cancelled { background: var(--color-text-tertiary); }


.runFooterControls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

/* ── Split button ── */
.splitBtn {
  position: relative;
  display: inline-flex;
  align-items: stretch;
}

.splitBtnMain {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-right: none;
  border-radius: var(--radius-md) 0 0 var(--radius-md);
  background: var(--color-bg-surface);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out),
              border-color var(--duration-fast) var(--ease-out);
}
.splitBtnMain:hover:not(:disabled) {
  background: var(--color-bg-subtle);
}
.splitBtnMain:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.splitBtnCaret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}
.splitBtnCaret:hover:not(:disabled) {
  background: var(--color-bg-subtle);
}
.splitBtnCaret:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.splitDropdown {
  position: absolute;
  top: calc(100% + var(--space-1));
  left: 0;
  min-width: 120px;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0,0,0,.15));
  padding: var(--space-1);
  z-index: 20;
}

.splitDropdownItem {
  display: block;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}
.splitDropdownItem:hover {
  background: var(--color-bg-subtle);
}
.splitDropdownItem--active {
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
}

/* ── Auto toggle ── */
.autoToggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.cadenceLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.autoLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
}

/* ── Auto-approve ── */
.autoApprove {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
}

.autoApproveLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  margin-right: auto;
}

.approvalWarningIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  color: var(--color-warning, #d97706);
  cursor: pointer;
}

.approvalWarningText {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  color: var(--color-text-secondary);
}

.approveConfirmText {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-3);
}

/* Standalone split-button (no separate caret button) for auto-approve. */
.autoApprove .splitBtnMain,
.autoToggle .splitBtnMain {
  border-right: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
}
</style>
